import {App, Stack, Tags, StackProps} from 'aws-cdk-lib'
import {Optional} from '@othree.io/optional'
import {IConstruct} from 'constructs'

export type StackInput = Readonly<{
    id: string
}>

export type Tag = Readonly<{
    key: string
    value: string
}>

export type StackConfiguration = Readonly<{
    tags: Array<Tag>
}>

/** Adds a CloudFormation tag to all resources within a construct scope. */
export const withTag = (deps: { scope: IConstruct }) =>
    (tag: Tag): Tag => {
        Tags.of(deps.scope).add(tag.key, tag.value)
        return tag
    }

export type ProjectMetadata = Readonly<{
    project: string
    environment: string
    owner: string
}>

/**
 * Builds default stack tags: Project, ApplicationId, Environment, Owner, and Version.
 * Version is read from `CI_COMMIT_REF_NAME` or falls back to `local-<USER>`.
 */
export const withDefaultConfiguration = (projectMetadata: ProjectMetadata, applicationId: string): StackConfiguration => {
    return {
        tags: [
            {
                key: 'Project',
                value: projectMetadata.project,
            },
            {
                key: 'ApplicationId',
                value: applicationId,
            },
            {
                key: 'Environment',
                value: projectMetadata.environment,
            },
            {
                key: 'Owner',
                value: projectMetadata.owner,
            },
            {
                key: 'Version',
                value: Optional(process.env.CI_COMMIT_REF_NAME).orElse(`local-${process.env.USER}`),
            },
        ],
    }
}

/** Creates a CloudFormation stack and applies default and optional additional tags. */
export const withStack = (deps: {
    defaultConfiguration: StackConfiguration
    app: App
}) =>
    (input: StackInput, configuration?: StackConfiguration, stackProperties?: StackProps): Stack => {
        const stack = new Stack(deps.app, input.id, stackProperties)
        const tagStack = withTag({ scope: stack })
        deps.defaultConfiguration.tags.forEach(tag => tagStack(tag))
        Optional(configuration).map(configuration => configuration!.tags.forEach(tag => tagStack(tag)))

        return stack
    }
